import {Component, EventEmitter, Input, Output} from "@angular/core";
import {AddressService} from "../address/address.service";
import {ContactService} from "../contact/contact.service";
import {OrderService} from "./order.service";
import {createEvent} from "../misc/event.polyfill";
import {SplitDeviceQuantityDialogComponent} from "./split-device-quantity-dialog.component";
import {DialogService} from "../bootstrap-modal/dialog.service";
import {exposeComponentInDebugMode} from "../misc/expose-component-in-debug-mode.decorator";
import {handleErrors} from "../misc/handle-errors.decorator";
import {
  deliveryMethodMap,
  OrderFormGqlModel,
  OrderFormLineItemModel,
  shippingDestinationMap,
} from "./order-form.gql-model";
import {GqlMicroFormMutationModelFactory, GqlMicroFormQueryModel} from "../graphql/graphql.model";
import gql from "graphql-tag";
import {DeviceOrderSplitResultModel, HardwareOrderDetailsInputModel} from "./order.gql-model";

@Component({
  selector: "order-form",
  templateUrl: "./order-form.template.pug",
  providers: [OrderService],
})
@exposeComponentInDebugMode()
export class OrderFormComponent {
  shippingDestinationValues = shippingDestinationMap;
  deliveryMethodValues = deliveryMethodMap;

  editingOrderResults: boolean = false;
  originalOrderResults: any[] = [];

  bulkUpdateOrderNumber: string = "";
  bulkUpdateOrderDate: string = "";

  @Input()
  orderForm: OrderFormGqlModel = new OrderFormGqlModel({});

  @Input()
  allowEditing: {[key: string]: boolean} = {};

  @Output("error")
  errorEmitter: EventEmitter<Event> = new EventEmitter();

  orderMicroFormQuery: any = gql`
    ${GqlMicroFormQueryModel.fragment}

    mutation updateOrderFormField($id: ID!, $paramName: String!, $paramValue: JSON!) {
      result: hardwareOrderField(id: $id, paramName: $paramName, paramValue: $paramValue) {
        ...microFormData
      }
    }
  `;

  orderFieldMutators = [
    "shippingDestination",
    "deliveryMethod",
    "salValNumber",
    "shipCompleted",
    "floorLevel",
    "estimatedDeliveryDate",
    "estimatedHardwareOrderDate",
    "targetDeliveryDate",
    "extraPricingInformation",
    "resellerInformation",
    "resellerInstructions",
    "deliveryInformation",
    "orderNotificationEmailList",
    "sourceDocumentationLink",
    "requestedDeliveryDate",
  ];

  microFormMutators: {[key: string]: GqlMicroFormMutationModelFactory} = this.orderFieldMutators.reduce(
    (obj, key) => {
      obj[key] = new GqlMicroFormMutationModelFactory(key, () => this.orderForm.id, this.orderMicroFormQuery, "result");
      return obj;
    },
    {},
  );

  // noinspection JSUnusedLocalSymbols - it's actually used
  private set error(error: Error) {
    const event = createEvent("error");
    event["error"] = error;
    this.errorEmitter.emit(event);
  }

  constructor(
    private orderService: OrderService,
    private addressService: AddressService,
    private contactService: ContactService,
    private dialogService: DialogService,
  ) {}

  forwardError(event): void {
    this.errorEmitter.emit(event);
  }

  @handleErrors()
  async editSiteAddress() {
    const {changed, address} = await this.addressService.modifyAddress("site", this.orderForm.siteId);

    if (changed) {
      this.orderForm.header.siteAddress = address;
    }
  }

  @handleErrors()
  async editInstallationContact() {
    const {changed, contact} = await this.contactService.modifyContact("installation", "order", this.orderForm.id);

    if (changed) {
      this.orderForm.header.installationContact = contact;
    }
  }

  @handleErrors()
  async editDeliveryContact() {
    const {changed, contact} = await this.contactService.modifyContact("delivery", "order", this.orderForm.id);

    if (changed) {
      this.orderForm.header.deliveryContact = contact;
    }
  }

  @handleErrors()
  async editConfigurationAddress() {
    const {changed, address} = await this.addressService.modifyAddress("order_configuration", this.orderForm.id);

    if (changed) {
      this.orderForm.header.configurationAddress = address;
    }
  }

  editOrderResults() {
    this.originalOrderResults = this.orderForm.devices.map(({orderNumber, orderDate}) => ({orderNumber, orderDate}));
    this.bulkUpdateOrderNumber = "";
    this.bulkUpdateOrderDate = "";
    this.editingOrderResults = true;
  }

  copyBulkOrderNumberAndDate() {
    if (this.bulkUpdateOrderNumber) {
      this.orderForm.devices.forEach((it) => { it.orderNumber = this.bulkUpdateOrderNumber; });
    }

    if (this.bulkUpdateOrderDate) {
      this.orderForm.devices.forEach((it) => { it.orderDate = this.bulkUpdateOrderDate; });
    }
  }

  cancelOrderResults() {
    this.orderForm.devices.forEach((it, index) => {
      if (!this.originalOrderResults[index]) return;
      it.orderNumber = this.originalOrderResults[index].orderNumber;
      it.orderDate = this.originalOrderResults[index].orderDate;
    });
    this.editingOrderResults = false;
  }

  @handleErrors()
  async saveOrderResults() {
    const updates = this.orderForm.devices
      .filter(it => !it.indented && !it.multipleOrderNumbers)
      .map(it => new HardwareOrderDetailsInputModel(it));

    await this.orderService.updateOrderNumbersAndDates(this.orderForm.id, updates);

    this.editingOrderResults = false;
  }

  handleSplitResult(
    index: number,
    items: OrderFormLineItemModel[],
    result: DeviceOrderSplitResultModel[],
    isAutoSplit = false,
  ) {
    if (!result || !result.length) return;

    const replacements = [];
    const spliceCount = items.length;

    for (const {splitId, quantity, orderNumber, orderDate} of result) {
      for (const row of items) {
        const output = new OrderFormLineItemModel(row);
        output.splitId = splitId;
        output.orderQuantity = quantity;
        output.extendedPrice = ((+row.cataloguePrice) * (+quantity)).toString(10);

        if (isAutoSplit) {
          output.setOrderNumberAndDate(orderNumber, orderDate);
        }

        replacements.push(output);
      }
    }

    this.orderForm.devices.splice(index, spliceCount, ...replacements);

    let lastShaded = true;

    for (const device of this.orderForm.devices) {
      if (!device.indented) {
        lastShaded = !lastShaded;
      }

      device.shaded = lastShaded;
    }
  }

  getItemsForSplit(index) {
    const target = this.orderForm.devices[index];
    const items = [target];

    for (let i = index + 1; i < this.orderForm.devices.length; i++) {
      const row = this.orderForm.devices[i];

      if (row && row.indented) {
        items.push(row);
      } else {
        break;
      }
    }

    return {items, target};
  }

  @handleErrors()
  async splitDevice(index) {
    const {items, target} = this.getItemsForSplit(index);

    const data = {
      totalQuantity: +target.orderQuantity,
      deviceId: target.id,
      splitId: target.splitId,
      siteOrderId: this.orderForm.id,
    };

    const result: DeviceOrderSplitResultModel[] =
      await SplitDeviceQuantityDialogComponent.show(this.dialogService, data);

    this.handleSplitResult(index, items, result, false);
  }

  @handleErrors()
  async autoSplitDevice(index) {
    const {items, target} = this.getItemsForSplit(index);

    const result: DeviceOrderSplitResultModel[] =
      await this.orderService.autoSplitDevice(target.id, target.splitId, this.orderForm.id);

    this.handleSplitResult(index, items, result, true);
  }
}
